/**
 * useLocale Composable
 *
 * Convenience wrapper that combines i18next-vue's `useTranslation()` with
 * the locale Pinia store for a unified API in Vue components.
 *
 * Exposes:
 *   • t()            — translation function ($t in templates)
 *   • locale         — reactive current language code (always 'en')
 *   • reloadBundle() — force bundle re-fetch after admin override change
 *
 * The backend bundle endpoint (GET /api/v1/locale/:domain) returns the
 * tenant's translations. There is no client-side language switching.
 * If the API fails or returns empty, the static 'en' fallback is used.
 *
 * Usage:
 * ```vue
 * <script setup lang="ts">
 * const { t, locale } = useLocale();
 * </script>
 *
 * <template>
 *   <h1>{{ t('nav.dashboard') }}</h1>
 * </template>
 * ```
 */
import { computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import { storeToRefs } from 'pinia';
import { useLocaleStore } from '@/stores/locale.store';

export function useLocale() {
  const { t, i18next } = useTranslation();
  const localeStore = useLocaleStore();
  const { currentLanguage, isLoading, error, isRtl, currentLanguageName } =
    storeToRefs(localeStore);

  /** Reactive current locale code. */
  const locale = computed(() => currentLanguage.value);

  /**
   * Force-reload the translation bundle from the API.
   *
   * Call this after an admin saves terminology overrides via
   * PUT /api/v1/locale/override (Override API), so the UI
   * reflects the updated terms immediately.
   *
   * Flow: i18next.reloadResources() → GET /api/v1/locale/:domain
   */
  async function reloadBundle(): Promise<void> {
    await localeStore.reloadAfterOverrideChange();
  }

  return {
    /** Translation function — use as `t('key')` or `t('key', { name: 'Alice' })`. */
    t,

    /** The raw i18next instance (advanced usage). */
    i18next,

    /** Reactive current language code (always 'en'). */
    locale,

    /** Reactive current language display name. */
    currentLanguageName,

    /** Reactive loading state. */
    isLoading,

    /** Reactive error state. */
    error,

    /** Whether current language is RTL (always false). */
    isRtl,

    /** Force-reload bundle (call after admin override change). */
    reloadBundle,
  };
}
